import { EndpointFormModal } from '@/components/admin/EndpointFormModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { LlmEndpoint, Provider } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Pencil,
  Power,
  Star,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  gcp: 'GCP Vertex AI (Gemini)',
  'gcp-anthropic': 'GCP Vertex AI (Claude)',
  vllm: 'vLLM',
  mlx: 'mlx',
  ollama: 'Ollama',
  'lm-studio': 'LM Studio',
  'openai-compatible': 'OpenAI 호환',
};

interface Props {
  id: string;
}

export function AdminLlmEndpointDetail({ id }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'llm-endpoints', id],
    queryFn: () => api.adminLlmGet(id),
  });

  const updateMut = useMutation({
    mutationFn: (body: Parameters<typeof api.adminLlmUpdate>[1]) => api.adminLlmUpdate(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] }),
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.'),
  });

  const probeMut = useMutation({
    mutationFn: () => api.adminLlmProbe(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] }),
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '연결 테스트에 실패했습니다.'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.adminLlmDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
      navigate({ to: '/admin/llm/endpoints' });
    },
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.'),
  });

  return (
    <PageShell
      header={
        <>
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/llm/endpoints' })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            LLM 엔드포인트 목록
          </button>
          <PageHeader
            title={data ? (data.displayName ?? data.name) : '엔드포인트'}
            description={data?.description ?? undefined}
          />
        </>
      }
    >
      {serverError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="ml-3 text-xs underline"
          >
            닫기
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '엔드포인트를 불러오지 못했습니다.'}
        </p>
      ) : data ? (
        <div className="space-y-4">
          <ActionsBar
            endpoint={data}
            onEdit={() => setEditOpen(true)}
            onProbe={() => probeMut.mutate()}
            probing={probeMut.isPending}
            onToggleEnabled={() => updateMut.mutate({ isEnabledForUsers: !data.isEnabledForUsers })}
            onToggleDefault={() =>
              data.isDefault ? undefined : updateMut.mutate({ isDefault: true })
            }
            onDelete={() => {
              if (window.confirm(`엔드포인트 "${data.displayName ?? data.name}" 을 삭제할까요?`)) {
                deleteMut.mutate();
              }
            }}
          />

          <StatusCard endpoint={data} />

          <InfoCard endpoint={data} />
        </div>
      ) : null}

      <EndpointFormModal
        open={editOpen}
        mode={editOpen && data ? { kind: 'edit', endpoint: data } : null}
        onClose={() => setEditOpen(false)}
      />
    </PageShell>
  );
}

function ActionsBar({
  endpoint,
  onEdit,
  onProbe,
  probing,
  onToggleEnabled,
  onToggleDefault,
  onDelete,
}: {
  endpoint: LlmEndpoint;
  onEdit: () => void;
  onProbe: () => void;
  probing: boolean;
  onToggleEnabled: () => void;
  onToggleDefault: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button onClick={onEdit}>
        <Pencil className="size-3.5" />
        편집
      </Button>
      <Button variant="secondary" onClick={onProbe} disabled={probing}>
        {probing ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <CheckCircle2 className="size-3.5" />
        )}
        연결 테스트
      </Button>
      <Button variant="secondary" onClick={onToggleEnabled}>
        <Power className="size-3.5" />
        {endpoint.isEnabledForUsers ? '사용자 노출 해제' : '사용자에게 노출'}
      </Button>
      <Button variant="secondary" onClick={onToggleDefault} disabled={endpoint.isDefault}>
        <Star className="size-3.5" />
        {endpoint.isDefault ? '기본 모델 (현재)' : '기본 모델로 지정'}
      </Button>
      <Button variant="ghost" onClick={onDelete} className="text-destructive">
        <Trash2 className="size-3.5" />
        삭제
      </Button>
    </div>
  );
}

function StatusCard({ endpoint }: { endpoint: LlmEndpoint }) {
  const has = endpoint.lastProbedAt !== null;
  const ok = endpoint.lastProbeOk === true;
  const skipped = ok && endpoint.lastProbeModelCount === null;
  const tone = !has
    ? 'border-border bg-surface'
    : skipped
      ? 'border-border bg-surface'
      : ok
        ? 'border-success/30 bg-success/5'
        : 'border-destructive/30 bg-destructive/5';
  return (
    <section className={cn('rounded-2xl border px-4 py-3', tone)}>
      <h2 className="text-xs uppercase tracking-wider text-foreground-muted">연결 상태</h2>
      {!has ? (
        <p className="mt-1 text-sm text-foreground-muted">
          아직 연결 테스트가 수행되지 않았습니다.
        </p>
      ) : skipped ? (
        <p className="mt-1 text-sm text-foreground-muted">
          외부 LLM — 자가체크 생략 (비용 절감). 수동 연결 테스트 버튼은 그대로 동작합니다.
        </p>
      ) : ok ? (
        <div className="mt-1 flex items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 text-success" />
          <span>정상 — 모델 {endpoint.lastProbeModelCount ?? 0} 개 발견</span>
          <span className="text-foreground-muted text-xs">
            ({formatRelative(endpoint.lastProbedAt)})
          </span>
        </div>
      ) : (
        <div className="mt-1 flex items-start gap-2 text-sm">
          <XCircle className="size-4 text-destructive mt-0.5" />
          <div>
            <div>실패 — {endpoint.lastProbeError ?? '알 수 없는 오류'}</div>
            <div className="text-foreground-muted text-xs">
              ({formatRelative(endpoint.lastProbedAt)})
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function InfoCard({ endpoint }: { endpoint: LlmEndpoint }) {
  return (
    <section className="rounded-2xl border border-border bg-surface px-4 py-3">
      <h2 className="text-xs uppercase tracking-wider text-foreground-muted mb-2">정보</h2>
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <Field label="이름">{endpoint.name}</Field>
        <Field label="표시명">{endpoint.displayName ?? '—'}</Field>
        <Field label="Provider">{PROVIDER_LABELS[endpoint.provider]}</Field>
        <Field label="모델">
          <code className="font-mono text-xs">{endpoint.model}</code>
        </Field>
        <Field label="Base URL">
          <code className="font-mono text-xs break-all">{endpoint.baseUrl}</code>
        </Field>
        <Field label="API 키">{endpoint.hasApiKey ? '설정됨' : '없음'}</Field>
        <Field label="Extended Thinking">{endpoint.extendedThinking ? '활성' : '비활성'}</Field>
        <Field label="Vision (이미지 입력)">{endpoint.supportsVision ? '지원' : '미지원'}</Field>
        <Field label="이미지 생성">{endpoint.supportsImageGeneration ? '지원' : '미지원'}</Field>
        <Field label="정렬 순서">{endpoint.sortOrder}</Field>
        <Field label="플래그">
          <div className="flex flex-wrap items-center gap-1.5">
            {endpoint.isDefault ? <Badge tone="accent">기본</Badge> : null}
            {endpoint.isVisionDefault ? <Badge tone="accent">Vision 기본</Badge> : null}
            {endpoint.supportsVision ? <Badge tone="ok">Vision</Badge> : null}
            {endpoint.isImageGenerationDefault ? (
              <Badge tone="accent">이미지생성 기본</Badge>
            ) : null}
            {endpoint.supportsImageGeneration ? <Badge tone="ok">이미지생성</Badge> : null}
            {endpoint.isEnabledForUsers ? (
              <Badge tone="ok">사용자 노출</Badge>
            ) : (
              <Badge tone="mute">숨김</Badge>
            )}
          </div>
        </Field>
        {Object.keys(endpoint.extraHeaders).length > 0 ? (
          <Field label="추가 헤더" wide>
            <pre className="font-mono text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(endpoint.extraHeaders, null, 2)}
            </pre>
          </Field>
        ) : null}
        <Field label="생성">{new Date(endpoint.createdAt).toLocaleString()}</Field>
        <Field label="수정">{new Date(endpoint.updatedAt).toLocaleString()}</Field>
      </dl>
    </section>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-[11px] uppercase tracking-wider text-foreground-muted">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'accent' | 'ok' | 'mute';
}) {
  const cls = {
    accent: 'bg-accent/15 text-accent',
    ok: 'bg-success/15 text-success',
    mute: 'bg-surface-muted text-foreground-muted',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider',
        cls,
      )}
    >
      {children}
    </span>
  );
}

export function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return new Date(iso).toLocaleString();
  const sec = Math.floor(ms / 1000);
  if (sec < 5) return '방금';
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  return new Date(iso).toLocaleString();
}
